"use client";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { ASSETS_URL } from "@/config/deployConstants";
import br from "@/styles/breakpoints.module.css";
import { themeColors } from "@/utils/themeColors";
import { Class, ClassSection, UserDetails } from "@knowt/syncing/graphql/schema";
import { callGetClass } from "@knowt/syncing/hooks/classes/graphqlUtils";
import { joinClass } from "@knowt/syncing/hooks/classes/utils";
import { LocalAuthUser } from "@knowt/syncing/hooks/user/types";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { usePublicProfile } from "@knowt/syncing/hooks/user/useUserData";
import Image from "next/image";
import { useRouter } from "@bprogress/next";
import { useState } from "react";
import toast from "react-hot-toast";
import { AuthInputCode } from "../Auth/components/styled/Forms";
import styles from "./joinClassPage.module.css";
import OutlineTextButton from "@/components/CircularButton/styled/OutlineTextButton";
import { useGoBack } from "@/utils/navigation/utils";
import Mountable from "@/components/styled/div/Mountable";
import Select from "@/components/Select";
import LoadingAnimation from "@/components/LoadingAnimation";
import { clsx } from "clsx";

enum JoinClassStep {
    ENTER_CODE,
    JOIN_SECTION,
}

const EnterClassCode = ({
    initialClassId,
    onConfirm,
    onCancel,
}: { initialClassId?: string; onConfirm: (props: { classId: string }) => Promise<void>; onCancel: () => void }) => {
    const [classId, setClassId] = useState(initialClassId || "");

    return (
        <>
            <FlexColumn style={{ maxWidth: "47rem" }}>
                <h1
                    className="heading4"
                    style={{ marginBottom: "0.8rem" }}
                    data-testid="auto-id-57fd11b1-84ab-4815-99bc-07383bccf4dc">
                    Type in your class code
                </h1>
                <p
                    className="body2"
                    style={{ marginBottom: "2.5rem" }}
                    data-testid="auto-id-f7b2dc32-90f9-4dc8-8821-1bdc648678f8">
                    Ask your teacher for the class code and enter it below to join.
                </p>
                <FlexRowAlignCenter
                    className={br.mdDownWrap}
                    style={{ gap: "1.4rem", marginBottom: "2.5rem", width: "fit-content", minHeight: "14.5rem" }}>
                    <AuthInputCode
                        defaultValue={initialClassId}
                        className={styles.smDownClassCodeInput}
                        style={{
                            backgroundColor: themeColors.background,
                            border: `2px solid ${themeColors.neutral1}`,
                            width: "6.5rem",
                            height: "7.9rem",
                        }}
                        length={6}
                        onChange={newClassId => setClassId(newClassId)}
                        anyCharacter
                    />
                </FlexRowAlignCenter>
                <FlexRowAlignCenter style={{ gap: "1rem", marginLeft: "auto" }}>
                    <OutlineTextButton
                        className="secondaryTextBold1"
                        onClick={onCancel}
                        sx={{ width: "9.8rem", height: "4rem" }}>
                        Cancel
                    </OutlineTextButton>
                    <CircularRectTextButton
                        onClick={async () => await onConfirm({ classId })}
                        className="secondaryTextBold1"
                        sx={{
                            width: "14.7rem",
                            height: "4rem",
                            backgroundColor: themeColors.primary,
                            color: themeColors.primaryDark,
                        }}>
                        Confirm Code
                    </CircularRectTextButton>
                </FlexRowAlignCenter>
            </FlexColumn>
            <Image
                src={`${ASSETS_URL}/images/kai-waving.svg`}
                alt="join class"
                width={300}
                height={380}
                style={{ padding: "4rem", objectFit: "contain", maxWidth: "100%" }}
            />
        </>
    );
};

const JoinSection = ({
    course,
    owner,
    onConfirm,
    onCancel,
}: {
    course: Class;
    owner: UserDetails;
    onConfirm: (props: { section: ClassSection }) => Promise<void>;
    onCancel: () => void;
}) => {
    const sections = course.sections.map(section => {
        return { label: section.name, value: section };
    });
    const [selectedSection, setSelectedSection] = useState(sections[0]);

    return (
        <>
            <FlexColumn style={{ maxWidth: "47rem" }}>
                <h1
                    className="heading4"
                    style={{ marginBottom: "0.8rem" }}
                    data-testid="auto-id-9491713e-d4fe-4b30-9065-efa12a1753fc">
                    Join {course.name}
                </h1>
                <p
                    className={clsx("body2", br.smDownWidth100)}
                    style={{ marginBottom: "2.5rem", width: "47rem" }}
                    data-testid="auto-id-797d6167-d0e0-4162-a004-d96ade2a2b27">
                    Select your section to join {owner.Name}'s {course.name}
                </p>
                <FlexColumn
                    style={{ marginBottom: "2.5rem", height: "13.6rem", width: "100%", justifyContent: "center" }}>
                    <p
                        className="secondaryTextBold1"
                        style={{ marginBottom: "0.8rem" }}
                        data-testid="auto-id-3fe57d98-9d39-4c4c-8b6e-ac1d44004c5f">
                        What section are you in?
                    </p>
                    <Select
                        btnSx={{
                            border: `1px solid ${themeColors.neutral1}`,
                            borderRadius: "4rem",
                            justifyContent: "flex-start",
                        }}
                        transitionOrigin={{
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                        }}
                        adornmentProps={{ style: { marginLeft: "auto" } }}
                        menuSx={{ width: "27rem" }}
                        selected={selectedSection}
                        options={sections}
                        onChange={value => setSelectedSection(value)}
                        data-testid="auto-id-c0ab4b0b-cb01-497e-a32e-5d7c37e04a50"
                    />
                </FlexColumn>
                <FlexRowAlignCenter style={{ gap: "1rem", marginLeft: "auto" }}>
                    <OutlineTextButton
                        className="secondaryTextBold1"
                        onClick={onCancel}
                        sx={{ width: "9.8rem", height: "4rem" }}>
                        Back
                    </OutlineTextButton>
                    <CircularRectTextButton
                        onClick={async () => await onConfirm({ section: selectedSection.value })}
                        className="secondaryTextBold1"
                        sx={{
                            width: "14.7rem",
                            height: "4rem",
                            backgroundColor: themeColors.primary,
                            color: themeColors.primaryDark,
                        }}>
                        Confirm Code
                    </CircularRectTextButton>
                </FlexRowAlignCenter>
            </FlexColumn>
            <Image
                src={`${ASSETS_URL}/images/kai-waving.svg`}
                alt="join class"
                width={300}
                height={380}
                style={{ padding: "4rem", objectFit: "contain", maxWidth: "100%" }}
            />
        </>
    );
};

const AuthJoinClassPage = ({
    serverClass,
    serverUser,
    initialClassId,
}: { serverClass: Class | null; serverUser: LocalAuthUser; initialClassId?: string }) => {
    const { userId, user, isTeacher } = useCurrentUser({ fallbackData: serverUser });
    const goBack = useGoBack();
    const router = useRouter();

    const [step, setStep] = useState<JoinClassStep>(
        initialClassId ? JoinClassStep.JOIN_SECTION : JoinClassStep.ENTER_CODE
    );

    const [course, setCourse] = useState<Class | null>(serverClass);
    const { userData: classOwnerData } = usePublicProfile(course?.userId);

    const handleJoinClass = async ({ classId }: { classId: string }) => {
        const course = await callGetClass({ classId });

        if (user?.classes?.enrolled?.some(classEnrollment => classEnrollment.classId === classId)) {
            toast.success("You are already enrolled in this class");
            router.push(`/class/${classId}/dashboard`);
            return;
        }

        if (!course) {
            toast.error("Class not found");
            return;
        }

        setCourse(course);

        // if there is only one section, skip the join section step. directly joining the class
        if (course.sections.length === 1) {
            handleJoinClassSection({ section: course.sections[0].id, classId: course.classId });
            return;
        }

        setStep(JoinClassStep.JOIN_SECTION);
    };

    const handleJoinClassSection = async ({ section, classId }: { section?: string; classId: string }) => {
        if (!classId) {
            return toast.error("Please enter a class code to continue");
        }

        if (!section && !isTeacher) {
            return toast.error("Please select a section to continue");
        }

        toast.loading("Joining class...", { id: "join-class" });
        try {
            await joinClass({ classId, sections: section ? [section] : [], userId });
            toast.success("You have successfully joined the class!", { id: "join-class" });
            router.push(`/class/${classId}/dashboard`);
        } catch (e) {
            if (e.message.includes("Organization does not match")) {
                toast.error("You cannot join this class because it belongs to a different organization", {
                    id: "join-class",
                });
                return;
            }

            toast.error(e?.message, { id: "join-class" });
        }
    };

    return (
        <FlexRowAlignCenter
            className={clsx(br.smDownColumnReverse, br.smDownWidth100)}
            style={{
                padding: "4rem",
                backgroundColor: themeColors.neutralWhite,
                gap: "1rem 4rem",
                maxWidth: "84.6rem",
                margin: "auto",
                borderRadius: "2rem",
                border: `1px solid ${themeColors.neutral1}`,
            }}>
            <Mountable mount={step === JoinClassStep.ENTER_CODE}>
                <EnterClassCode
                    initialClassId={initialClassId}
                    onCancel={() => goBack()}
                    onConfirm={async ({ classId }) => handleJoinClass({ classId })}
                />
            </Mountable>
            <Mountable mount={step === JoinClassStep.JOIN_SECTION && !!course}>
                {classOwnerData ? (
                    <JoinSection
                        course={course as Class}
                        owner={classOwnerData as UserDetails}
                        onCancel={() => setStep(JoinClassStep.ENTER_CODE)}
                        onConfirm={async ({ section }) => {
                            await handleJoinClassSection({ section: section.id, classId: course?.classId as string });
                        }}
                    />
                ) : (
                    <LoadingAnimation />
                )}
            </Mountable>
        </FlexRowAlignCenter>
    );
};

export default AuthJoinClassPage;
